/**
 * Reusable Zod primitives.
 *
 * Every request that reaches the API is validated with these. Defining them
 * once means "what is a valid amount" has exactly one answer, on both sides of
 * the wire.
 */

import { z } from 'zod';
import { COUNTRY_CODES, CURRENCY_CODES } from './locales.js';
import { MONEY_SCALE, toMinor } from './money.js';

export const uuidSchema = z.string().uuid('Must be a valid id');

export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3)
  .max(255)
  .email('Enter a valid email address');

/**
 * Passwords: length is the only requirement that reliably correlates with
 * strength, so we ask for 10 characters rather than a composition rule that
 * pushes people toward "Password1!".
 */
export const passwordSchema = z
  .string()
  .min(10, 'Password must be at least 10 characters')
  .max(200, 'Password must be at most 200 characters');

/** Loose E.164-ish. Deliberately permissive — this is a business contact field. */
export const phoneSchema = z
  .string()
  .trim()
  .min(6, 'Phone number is too short')
  .max(32, 'Phone number is too long')
  .regex(/^\+?[0-9\s()\-.]+$/, 'Phone number contains invalid characters');

export const shortTextSchema = z.string().trim().min(1).max(200);
export const longTextSchema = z.string().trim().max(2000);

export const countryCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .refine((v) => COUNTRY_CODES.includes(v), 'Unsupported country');

export const currencyCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .refine((v) => CURRENCY_CODES.includes(v), 'Unsupported currency');

export const timezoneSchema = z
  .string()
  .trim()
  .max(64)
  .refine((v) => {
    try {
      new Intl.DateTimeFormat('en-US', { timeZone: v });
      return true;
    } catch {
      return false;
    }
  }, 'Unknown timezone');

/**
 * A money amount on the wire. Accepts a decimal string or a number, always
 * emits the canonical 2-decimal string. Rejects values with more precision
 * than we can store rather than rounding them behind the user's back.
 */
export const moneySchema = z
  .union([z.string(), z.number()])
  .transform((v, ctx) => {
    try {
      const minor = toMinor(v);
      const negative = minor < 0n;
      const abs = negative ? -minor : minor;
      const whole = abs / 100n;
      const frac = abs % 100n;
      return `${negative ? '-' : ''}${whole}.${frac.toString().padStart(MONEY_SCALE, '0')}`;
    } catch (error) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: error instanceof Error ? error.message : 'Invalid amount',
      });
      return z.NEVER;
    }
  });

/**
 * Compare a transformed money value without ever throwing.
 *
 * Zod runs a `.refine()` even when the preceding `.transform()` failed, and in
 * that case hands it Zod's internal INVALID sentinel rather than a string.
 * Calling `toMinor` on that raises a TypeError which escapes validation
 * entirely and surfaces as a 500 — so a mistyped amount would crash the
 * request instead of being reported as a bad field. Guarding here keeps every
 * malformed amount on the 422 path.
 */
function compareMoney(value: unknown, test: (minor: bigint) => boolean): boolean {
  if (typeof value !== 'string') return false;
  try {
    return test(toMinor(value));
  } catch {
    return false;
  }
}

/** Money that must be greater than zero — sale and expense amounts. */
export const positiveMoneySchema = moneySchema.refine(
  (v) => compareMoney(v, (minor) => minor > 0n),
  'Amount must be greater than zero',
);

/** Money that may be zero but never negative — costs, partial payments. */
export const nonNegativeMoneySchema = moneySchema.refine(
  (v) => compareMoney(v, (minor) => minor >= 0n),
  'Amount cannot be negative',
);

/** An ISO-8601 instant. Accepts a Date too, for convenience in tests. */
export const isoDateTimeSchema = z
  .union([z.string().datetime({ offset: true }), z.date()])
  .transform((v) => (v instanceof Date ? v.toISOString() : new Date(v).toISOString()));

/**
 * A calendar date (YYYY-MM-DD), interpreted in the business's timezone.
 *
 * Shape AND calendar validity. The shape check alone let `2026-13-45` and
 * `2026-02-31` through, and what happened next depended on which half of the
 * codebase received them:
 *
 *   - in JavaScript, `new Date(Date.UTC(2026, 12, 45))` rolls over silently to
 *     **2027-02-14**. The caller asked for a day that does not exist and got a
 *     confident, empty answer about a different month fourteen months away.
 *   - in SQL, `'2026-13-45'::date` raises 22008 and the request became a 500
 *     with "Something went wrong".
 *
 * Neither is an answer. A date that is not a date is the caller's mistake and
 * should be refused by name, once, here — so that every endpoint taking a date
 * inherits the refusal instead of each one discovering its own failure mode.
 */
export const isoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected a date in YYYY-MM-DD format')
  .refine((v) => {
    // Round-tripping is the check: a real date renders back as itself, and a
    // rolled-over one does not. Cheaper and more honest than re-deriving the
    // length of February.
    const [y, m, d] = v.split('-').map(Number) as [number, number, number];
    const probe = new Date(Date.UTC(y, m - 1, d));
    return (
      probe.getUTCFullYear() === y &&
      probe.getUTCMonth() === m - 1 &&
      probe.getUTCDate() === d
    );
  }, 'That date does not exist in the calendar');

// ── Pagination ──────────────────────────────────────────────────────────────

export const DEFAULT_PAGE_SIZE = 25;
export const MAX_PAGE_SIZE = 200;

export const paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
});

export type Pagination = z.infer<typeof paginationSchema>;

export interface Paginated<T> {
  data: T[];
  meta: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export const sortOrderSchema = z.enum(['asc', 'desc']).default('desc');

/**
 * A date window used by list filters, the dashboard and every report.
 * Both bounds are inclusive and expressed as calendar dates in the business's
 * timezone; the API converts them to UTC instants at query time.
 */
/** The raw fields, so other schemas can `.merge()` them into their own filters. */
export const dateRangeFields = z.object({
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
});

/**
 * Guards against an inverted window, which would silently return nothing.
 *
 * Applied as `.refine(isOrderedRange, ORDERED_RANGE_ISSUE)` directly on each
 * schema rather than through a wrapper function. A generic wrapper collapses
 * the schema's inferred output to its constraint, which quietly turns every
 * `.default()`-ed field back into `| undefined` at the call site.
 */
export const isOrderedRange = (v: { from?: string | undefined; to?: string | undefined }): boolean =>
  !v.from || !v.to || v.from <= v.to;

export const ORDERED_RANGE_ISSUE = {
  message: '"from" must be on or before "to"',
  path: ['from'] as const,
} satisfies { message: string; path: readonly string[] };

export const dateRangeSchema = dateRangeFields.refine(isOrderedRange, {
  message: ORDERED_RANGE_ISSUE.message,
  path: ['from'],
});

export type DateRange = z.infer<typeof dateRangeFields>;
